'use client'

import { TimedCartProvider } from '@/context/TimedCartContext'
import { NavbarExtrasProvider, useNavbarExtras } from '@/context/NavbarExtrasContext'
import CartDrawer from '@/components/timed-ui/cart/CartDrawer'
import Navbar from '@/components/timed-ui/layout/Navbar'
import CartButton from '@/components/timed-ui/cart/CartButton'
import LocationsNavMenu from '@/components/timed-ui/layout/LocationsNavMenu'
import { useTheme } from '@/components/public/branding/ThemeProvider'
import DynamicStyles from '@/components/public/branding/DynamicStyles'
import FooterSocialLinks from '@/components/public/layout/FooterSocialLinks'
import PublicNavLinks from '@/components/public/layout/PublicNavLinks'
import { sanitizeBrandingHtml } from '@/lib/branding/sanitize'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import EmbedHeightReporter from '@/components/public/embed/EmbedHeightReporter'
import SiteGuards from '@/components/public/SiteGuards'

function PublicLayoutShell({ children }: { children: React.ReactNode }) {
  const { branding } = useTheme()
  const { searchContent, checkoutRight } = useNavbarExtras()
  const pathname = usePathname()

  // Embed mode: when the storefront is loaded inside an embedded checkout modal
  // (?embed=1), strip the site nav + footer so it reads as a clean checkout
  // panel, not a whole webpage in a box. Latched in sessionStorage so it
  // survives the event -> checkout -> success client navigations inside the
  // iframe. Gated strictly on the flag; normal visits are unaffected.
  const searchParams = useSearchParams()
  const [embedMode, setEmbedMode] = useState(false)
  useEffect(() => {
    try {
      if (searchParams?.get('embed') === '1') {
        sessionStorage.setItem('pt_embed', '1')
        setEmbedMode(true)
      } else if (sessionStorage.getItem('pt_embed') === '1') {
        setEmbedMode(true)
      }
    } catch { /* sessionStorage unavailable */ }
  }, [searchParams])

  const siteName = branding?.publicSite?.siteName || ''
  const logoUrl = branding?.assets?.logoUrl || null
  const isCheckout = pathname?.startsWith('/checkout')
  const isConfirmation = pathname?.startsWith('/checkout/success')

  // Structured footer (branding.publicSite.footer) replaces reliance on the
  // legacy raw footerHTML. footerHTML is kept only as a fallback when no
  // structured columns are configured, to avoid regressing existing tenants.
  const publicSite = (branding?.publicSite || {}) as Record<string, unknown>
  const footer = (publicSite.footer || {}) as {
    columns?: Array<{ heading?: string; links?: Array<{ label?: string; url?: string; openInNewTab?: boolean }> }>
    showSocialLinks?: boolean
    copyright?: string
  }
  const footerColumns = Array.isArray(footer.columns) ? footer.columns : []
  const footerCopyright = typeof footer.copyright === 'string' ? footer.copyright : ''
  const contactEmail = typeof publicSite.contactEmail === 'string' ? publicSite.contactEmail.trim() : ''
  // Absent => true (default behavior), explicit false => hide.
  const showSocialLinks = footer.showSocialLinks !== false
  const socialLinks = (publicSite.socialLinks || null) as {
    facebook?: string; twitter?: string; instagram?: string
    linkedin?: string; youtube?: string; tiktok?: string
  } | null

  // Navbar custom links + built-in About/Contact/Help toggles
  // (branding.publicSite.navigation). Custom links are hidden during checkout.
  const navigation = (publicSite.navigation || {}) as {
    showAbout?: boolean; showContact?: boolean; showHelp?: boolean
    customLinks?: Array<{ label: string; url: string; openInNewTab?: boolean }>
  }

  // Feature flags (branding.publicSite.features). Absent/true preserves current
  // behavior; explicit false hides the surface.
  const features = (publicSite.features || {}) as Record<string, unknown>
  const showEventSearch = features.showEventSearch !== false

  // Chromeless render for embedded checkout — content only, no nav/footer.
  if (embedMode) {
    return (
      <TimedCartProvider>
        <DynamicStyles />
        <EmbedHeightReporter />
        <div className="bg-surface-secondary w-full max-w-full flex flex-col">
          <main className="w-full max-w-full flex-1">{children}</main>
        </div>
        <CartDrawer />
      </TimedCartProvider>
    )
  }

  return (
    <TimedCartProvider>
      <DynamicStyles />
      <div className="bg-surface-secondary w-full max-w-full min-h-screen flex flex-col">
        <Navbar
          title={isCheckout ? undefined : (siteName || undefined)}
          logoUrl={isCheckout ? null : logoUrl}
          showCart={false}
          showSignIn={!isCheckout}
          searchContent={isCheckout || !showEventSearch ? undefined : searchContent}
          rightContent={isCheckout ? checkoutRight : (
            <>
              <PublicNavLinks customLinks={navigation.customLinks} />
              <LocationsNavMenu />
              <CartButton />
            </>
          )}
          hideRightContent={false}
          backLabel={isConfirmation ? 'Back to Events' : isCheckout ? 'Back' : undefined}
          backTo={isConfirmation ? '/' : undefined}
          centerContent={isCheckout ? (
            <div className="hidden lg:flex items-center gap-1.5 text-text-tertiary text-xs">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>Secure Checkout</span>
            </div>
          ) : undefined}
        />
        <main className="w-full max-w-full flex-1">
          {children}
        </main>
        <footer
          className="public-footer py-6"
          style={{
            backgroundColor: branding?.colors?.footerBackground || 'rgb(var(--color-hero-bg))',
            color: branding?.colors?.footerText || (branding?.colors?.footerBackground ? (() => {
              const hex = branding.colors.footerBackground
              const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16)
              return (r * 299 + g * 587 + b * 114) / 1000 > 128 ? '#111827' : '#ffffff'
            })() : 'rgb(var(--color-text-secondary))'),
            paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
          }}
        >
          <div className="max-w-page mx-auto px-4 lg:px-8 text-sm">
            {footerColumns.length > 0 ? (
              <div className="flex flex-col gap-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 text-left">
                  {footerColumns.map((col, ci) => (
                    <div key={ci} className="flex flex-col gap-2">
                      {col.heading ? (
                        <h3 className="font-semibold text-sm" style={{ color: 'inherit' }}>{col.heading}</h3>
                      ) : null}
                      <ul className="flex flex-col gap-1.5">
                        {(col.links || []).map((link, li) => (
                          <li key={li}>
                            <a
                              href={link.url || '#'}
                              {...(link.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                              className="opacity-70 hover:opacity-100 transition-opacity"
                            >
                              {link.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                {showSocialLinks && <FooterSocialLinks links={socialLinks} />}
                {contactEmail && (
                  <p className="text-center opacity-80">
                    <a href={`mailto:${contactEmail}`} className="hover:opacity-100 transition-opacity">{contactEmail}</a>
                  </p>
                )}
                <p className="text-center opacity-80">
                  {footerCopyright || `© ${new Date().getFullYear()} ${siteName} All rights reserved.`}
                </p>
              </div>
            ) : branding?.customizations?.footerHTML ? (
              <>
                <div className="text-center" dangerouslySetInnerHTML={{ __html: sanitizeBrandingHtml(branding.customizations.footerHTML) }} />
                {showSocialLinks && (
                  <div className="mt-4">
                    <FooterSocialLinks links={socialLinks} />
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col gap-4 text-center">
                {showSocialLinks && <FooterSocialLinks links={socialLinks} />}
                {contactEmail && (
                  <a href={`mailto:${contactEmail}`} className="opacity-80 hover:opacity-100 transition-opacity">{contactEmail}</a>
                )}
                <p>{footerCopyright || `© ${new Date().getFullYear()} ${siteName} All rights reserved.`}</p>
              </div>
            )}
          </div>
        </footer>
      </div>
      <CartDrawer />
    </TimedCartProvider>
  )
}

export default function PublicRootLayout({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <NavbarExtrasProvider>
      <PublicLayoutShell>{children}</PublicLayoutShell>
      <SiteGuards />
    </NavbarExtrasProvider>
  )
}
